import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Boxes,
  Users,
  Store,
  Fingerprint,
  Calculator,
  Check,
  Sparkles,
} from "lucide-react";
import { Reveal } from "./motion";

const pillars = [
  {
    icon: Boxes,
    tag: "ERP",
    title: "Gestão completa do negócio",
    subtitle:
      "Cadastro, estoque, financeiro, multi-filial e módulo fiscal completo com assistência do Talos.",
    bullets: [
      "Cadastro de produtos com foto e IA fiscal do Talos",
      "Categorias por filial com controle de acesso granular",
      "Histórico de estoque centralizado com alerta de ruptura",
      "Despesas categorizadas e conciliação bancária",
      "Multi-filial (de 1 até 30 filiais) com visão unificada",
      "NF-e / NFC-e, Simples ou Regime Normal, exportação contábil",
    ],
    accent: "from-navy-700 to-navy-900",
  },
  {
    icon: Store,
    tag: "PDV",
    title: "Frente de caixa ágil e inteligente",
    subtitle:
      "Abre, vende, emite e fecha. Estoque e financeiro atualizados na hora — no balcão ou no celular.",
    bullets: [
      "Abertura e fechamento de caixa com sangria e suprimento",
      "Múltiplas formas de pagamento (Pix, Cartão, Boleto, Dinheiro)",
      "Scanner de código de barras pela câmera do celular",
      "Terminal compartilhado com PIN individual por colaborador",
      "Vinculação rápida de cliente pelo CPF/CNPJ",
      "Relatório de fechamento com auditoria do Talos",
    ],
    accent: "from-navy-700 to-navy-900",
  },
  {
    icon: Users,
    tag: "CRM",
    title: "Relacionamento e aumento de vendas",
    subtitle:
      "Do primeiro contato ao pós-venda, com timeline unificada e sugestões comerciais do Talos.",
    bullets: [
      "Provedor de Tecnologia Meta: WhatsApp Business oficial em 1 clique",
      "Base unificada com histórico de compras no PDV",
      "Funil de vendas visual com estágios comerciais customizados",
      "Disparo oficial de ofertas, cobranças e pós-venda sem risco de bloqueio",
      "Segmentação automática de clientes inativos e aniversariantes",
      "Ranking de vendedores com metas em tempo real",
    ],
    accent: "from-navy-700 to-navy-900",
  },
  {
    icon: Fingerprint,
    tag: "PONTO DIGITAL",
    title: "Controle de ponto seguro e sem fraudes",
    subtitle:
      "Selfie com biometria facial, geolocalização e espelho de ponto 100% aderente ao MTP.",
    bullets: [
      "Registro de ponto por selfie com verificação facial",
      "Geolocalização com cerca virtual (geofencing) por filial",
      "Espelho de ponto consolidado e cálculo de banco de horas",
      "Auditoria automática de atrasos e horas extras pelo Talos",
      "100% em conformidade com a Portaria 671 do MTP",
      "Equipe e colaboradores ilimitados sem custo adicional",
    ],
    accent: "from-navy-700 to-navy-900",
  },
  {
    icon: Calculator,
    tag: "CONTABILIDADE",
    title: "Contabilidade preditiva em tempo real",
    subtitle:
      "O MUFT substitui o seu contador: a IA Talos faz o trabalho pesado nos bastidores e você gera tudo em 1 clique.",
    bullets: [
      "Holerites gerados e conferidos em 1 clique",
      "Rescisões com cálculo completo de verbas e multas",
      "Fechamento de folha com validação inteligente",
      "Cálculo de 13º salário e provisão mensal automática",
      "Comissões apuradas por vendedor e integradas à folha",
      "Transmissão para o eSocial com validação prévia",
    ],
    accent: "from-navy-700 to-navy-900",
  },
];

export const Features = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = pillars[activeIdx];
  const ActiveIcon = active.icon;

  return (
    <section
      id="modulos"
      className="relative bg-white py-24 transition-colors duration-500 dark:bg-navy-950 md:py-32"
      data-testid="features-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-lavender/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-navy-800 dark:border-white/10 dark:bg-navy-800 dark:text-lavender">
            <Sparkles className="h-3.5 w-3.5" /> Módulos Integrados
          </div>
          <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
            Tudo o que sua empresa precisa para rodar no piloto automático
          </h2>
          <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
            Chega de planilhas soltas e múltiplos softwares. O MUFT integra todos os departamentos da sua empresa em um só lugar.
          </p>
        </Reveal>

        {/* Modules Nav */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const isSelected = i === activeIdx;
            return (
              <button
                key={p.tag}
                onClick={() => setActiveIdx(i)}
                data-testid={`pillar-tab-${p.tag.toLowerCase()}`}
                className={`flex flex-col items-start rounded-2xl p-5 text-left transition-all duration-300 ${
                  isSelected
                    ? "bg-navy-800 text-white shadow-xl shadow-navy-800/25 scale-[1.02] dark:bg-white dark:text-navy-900"
                    : "border border-navy-100 bg-ice hover:border-navy-200 dark:border-white/10 dark:bg-navy-900/60 dark:text-white"
                }`}
              >
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl mb-3 ${
                    isSelected
                      ? "bg-lavender text-navy-800 dark:bg-navy-800 dark:text-lavender"
                      : "bg-white text-navy-800 dark:bg-white/10 dark:text-lavender shadow-sm"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider opacity-75">
                  Módulo 0{i + 1}
                </span>
                <span className="text-lg font-black mt-0.5">{p.tag}</span>
              </button>
            );
          })}
        </div>

        {/* Active Module Card */}
        <div className="mt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.tag}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3 }}
              className="rounded-3xl border border-navy-100 bg-gradient-to-br from-white to-ice p-8 shadow-xl dark:border-white/10 dark:from-navy-900 dark:to-navy-950 md:p-12"
            >
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-5 space-y-4">
                  <div className="inline-flex items-center gap-2 rounded-full bg-lavender/50 dark:bg-white/10 px-3 py-1 text-xs font-bold text-navy-800 dark:text-lavender">
                    <ActiveIcon className="h-4 w-4" />
                    <span>Módulo {active.tag}</span>
                  </div>
                  <h3 className="text-3xl font-black text-navy-800 dark:text-white sm:text-4xl">
                    {active.title}
                  </h3>
                  <p className="text-sm font-medium text-navy-400 dark:text-navy-100/70">
                    {active.subtitle}
                  </p>
                </div>

                <div className="lg:col-span-7">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {active.bullets.map((b, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2.5 rounded-2xl bg-white dark:bg-navy-800/80 p-4 border border-navy-50 dark:border-white/5 shadow-sm text-xs font-semibold text-navy-700 dark:text-navy-100"
                      >
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-navy-800 text-white dark:bg-lavender dark:text-navy-900 mt-0.5">
                          <Check className="h-2.5 w-2.5 stroke-[3]" />
                        </span>
                        <span className="leading-relaxed">{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Features;
